import React from 'react'
import {
  Box,
  BoxProps,
  Typography,
  useTheme,
  TypographyProps,
  SxProps,
  Theme,
} from '@mui/material'

interface Props {
  label: string
  value?: React.ReactNode
  variant?: 'body1' | 'body2'
  containerProps?: BoxProps
  labelProps?: TypographyProps & { sx?: SxProps<Theme> }
  valueProps?: TypographyProps & { sx?: SxProps<Theme> }
}

const LabelValue: React.FC<Props> = ({
  label,
  value,
  variant = 'body2',
  containerProps,
  labelProps,
  valueProps,
}) => {
  const theme = useTheme()

  const defaultLabelSx = {
    display: 'block',
    mb: 1,
    fontWeight: 600,
    color: theme.palette.textOrIcon.subheader,
  }

  const defaultValueSx: SxProps<Theme> = {
    color: theme.palette.primary.dark,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    flexWrap: 'wrap',
  }

  const { sx: labelSxFromProps, ...restLabelProps } = labelProps || {}
  const { sx: valueSxFromProps, ...restValueProps } = valueProps || {}

  const mergedLabelSx: SxProps<Theme> = Array.isArray(labelSxFromProps)
    ? [defaultLabelSx, ...labelSxFromProps]
    : [defaultLabelSx, labelSxFromProps]

  const mergedValueSx: SxProps<Theme> = Array.isArray(valueSxFromProps)
    ? [defaultValueSx, ...valueSxFromProps]
    : [defaultValueSx, valueSxFromProps]

  return (
    <Box {...containerProps}>
      <Typography
        variant={(labelProps && labelProps.variant) ?? 'overline'}
        sx={mergedLabelSx}
        {...restLabelProps}
      >
        {label}
      </Typography>
      <Box
        sx={{
          width: '100%',
          backgroundColor: theme.palette.imageUploadBackground?.main,
          p: 1.25,
          borderRadius: 1,
          border: `1px solid ${theme.palette.customBackground?.divider}`,
          minHeight: 40,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography
          variant={(valueProps && valueProps.variant) ?? variant}
          sx={mergedValueSx}
          {...restValueProps}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  )
}

export default LabelValue
